import React from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../context/ThemeContext';
import { createThemedStyles } from '../theme/useThemedStyles';
import { Goal, PriorityType } from '../context/GoalsContext';
import { isWeb } from '../utils/platformUtils';
import type { GoalTypeFilter, LoanTypeFilter, GoalStatusFilter } from '../hooks/useGoalFilters';
import { GOAL_PROGRESS_STATUSES, statusFilterColors } from '../utils/goalProgressStatus';

const goalTypeIcons: Record<Goal['type'], string> = {
  Savings: '💰',
  Debt: '💳',
  Investment: '📈',
};

const priorityColors: Record<PriorityType, string> = {
  Essential: '#FF4B4B',
  Important: '#FFA726',
  Optional: '#66BB6A',
};

export type GoalSearchFiltersProps = {
  goals: Goal[];
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  selectedTypeFilter: GoalTypeFilter;
  onTypeFilterChange: (type: GoalTypeFilter) => void;
  selectedPriorityFilter: PriorityType | 'all';
  onPriorityFilterChange: (priority: PriorityType | 'all') => void;
  selectedLoanTypeFilter: LoanTypeFilter;
  onLoanTypeFilterChange: (loanType: LoanTypeFilter) => void;
  selectedStatusFilter: GoalStatusFilter;
  onStatusFilterChange: (status: GoalStatusFilter) => void;
  showFilters: boolean;
  onToggleFilters: () => void;
  onClearFilters: () => void;
};

const GoalSearchFilters = ({
  goals,
  searchQuery,
  onSearchQueryChange,
  selectedTypeFilter,
  onTypeFilterChange,
  selectedPriorityFilter,
  onPriorityFilterChange,
  selectedLoanTypeFilter,
  onLoanTypeFilterChange,
  selectedStatusFilter,
  onStatusFilterChange,
  showFilters,
  onToggleFilters,
  onClearFilters,
}: GoalSearchFiltersProps) => {
  const styles = useGoalSearchFiltersStyles();
  const { colors } = useTheme();

  return (
    <>
      <View style={styles.searchFilterContainer}>
        <View style={styles.searchContainer}>
          <Ionicons name="search" size={20} color={colors.grey[500]} style={styles.searchIcon} />
          <TextInput
            style={styles.searchInput}
            placeholder="Search goals or status..."
            value={searchQuery}
            onChangeText={onSearchQueryChange}
            placeholderTextColor={colors.text.placeholder}
          />
          {searchQuery.length > 0 ? (
            <TouchableOpacity onPress={() => onSearchQueryChange('')} style={styles.clearButton}>
              <Ionicons name="close-circle" size={20} color={colors.grey[500]} />
            </TouchableOpacity>
          ) : null}
        </View>

        <TouchableOpacity style={styles.filterToggleButton} onPress={onToggleFilters}>
          <Ionicons name="filter" size={20} color={colors.button.primary} />
          <Text style={styles.filterToggleText}>Filters</Text>
          <Ionicons
            name={showFilters ? 'chevron-up' : 'chevron-down'}
            size={16}
            color={colors.button.primary}
          />
        </TouchableOpacity>
      </View>

      {showFilters ? (
        <View style={styles.filterOptionsContainer}>
          <View style={styles.filterSection}>
            <Text style={styles.filterSectionTitle}>Goal Type</Text>
            <View style={styles.filterChipsContainer}>
              <TouchableOpacity
                style={[styles.filterChip, selectedTypeFilter === 'all' && styles.filterChipSelected]}
                onPress={() => onTypeFilterChange('all')}
              >
                <Text
                  style={[
                    styles.filterChipText,
                    selectedTypeFilter === 'all' && styles.filterChipTextSelected,
                  ]}
                >
                  All
                </Text>
              </TouchableOpacity>
              {Object.entries(goalTypeIcons).map(([type, icon]) => (
                <TouchableOpacity
                  key={type}
                  style={[styles.filterChip, selectedTypeFilter === type && styles.filterChipSelected]}
                  onPress={() => onTypeFilterChange(type as Goal['type'])}
                >
                  <Text style={styles.filterChipIcon}>{icon}</Text>
                  <Text
                    style={[
                      styles.filterChipText,
                      selectedTypeFilter === type && styles.filterChipTextSelected,
                    ]}
                  >
                    {type}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          <View style={styles.filterSection}>
            <Text style={styles.filterSectionTitle}>Progress Status</Text>
            <View style={styles.filterChipsContainer}>
              <TouchableOpacity
                style={[
                  styles.filterChip,
                  selectedStatusFilter === 'all' && styles.filterChipSelected,
                ]}
                onPress={() => onStatusFilterChange('all')}
              >
                <Text
                  style={[
                    styles.filterChipText,
                    selectedStatusFilter === 'all' && styles.filterChipTextSelected,
                  ]}
                >
                  All
                </Text>
              </TouchableOpacity>
              {GOAL_PROGRESS_STATUSES.map(status => (
                <TouchableOpacity
                  key={status}
                  style={[
                    styles.filterChip,
                    selectedStatusFilter === status && styles.filterChipSelected,
                    { borderColor: statusFilterColors[status] },
                  ]}
                  onPress={() => onStatusFilterChange(status)}
                >
                  <Text
                    style={[
                      styles.filterChipText,
                      { color: statusFilterColors[status] },
                      selectedStatusFilter === status && styles.filterChipTextSelected,
                    ]}
                  >
                    {status}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          <View style={styles.filterSection}>
            <Text style={styles.filterSectionTitle}>Priority</Text>
            <View style={styles.filterChipsContainer}>
              <TouchableOpacity
                style={[
                  styles.filterChip,
                  selectedPriorityFilter === 'all' && styles.filterChipSelected,
                ]}
                onPress={() => onPriorityFilterChange('all')}
              >
                <Text
                  style={[
                    styles.filterChipText,
                    selectedPriorityFilter === 'all' && styles.filterChipTextSelected,
                  ]}
                >
                  All
                </Text>
              </TouchableOpacity>
              {(Object.keys(priorityColors) as PriorityType[]).map(priority => (
                <TouchableOpacity
                  key={priority}
                  style={[
                    styles.filterChip,
                    selectedPriorityFilter === priority && styles.filterChipSelected,
                    { borderColor: priorityColors[priority] },
                  ]}
                  onPress={() => onPriorityFilterChange(priority)}
                >
                  <Text
                    style={[
                      styles.filterChipText,
                      { color: priorityColors[priority] },
                      selectedPriorityFilter === priority && styles.filterChipTextSelected,
                    ]}
                  >
                    {priority}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {goals.some(goal => goal.type === 'Debt') ? (
            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>Loan Type</Text>
              <View style={styles.filterChipsContainer}>
                <TouchableOpacity
                  style={[
                    styles.filterChip,
                    selectedLoanTypeFilter === 'all' && styles.filterChipSelected,
                  ]}
                  onPress={() => onLoanTypeFilterChange('all')}
                >
                  <Text
                    style={[
                      styles.filterChipText,
                      selectedLoanTypeFilter === 'all' && styles.filterChipTextSelected,
                    ]}
                  >
                    All
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.filterChip,
                    selectedLoanTypeFilter === 'credit-card' && styles.filterChipSelected,
                  ]}
                  onPress={() => onLoanTypeFilterChange('credit-card')}
                >
                  <Text
                    style={[
                      styles.filterChipText,
                      selectedLoanTypeFilter === 'credit-card' && styles.filterChipTextSelected,
                    ]}
                  >
                    Credit Card
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.filterChip,
                    selectedLoanTypeFilter === 'fixed-term' && styles.filterChipSelected,
                  ]}
                  onPress={() => onLoanTypeFilterChange('fixed-term')}
                >
                  <Text
                    style={[
                      styles.filterChipText,
                      selectedLoanTypeFilter === 'fixed-term' && styles.filterChipTextSelected,
                    ]}
                  >
                    Fixed Term
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          ) : null}

          <TouchableOpacity style={styles.clearFiltersButton} onPress={onClearFilters}>
            <Text style={styles.clearFiltersButtonText}>Clear All Filters</Text>
          </TouchableOpacity>
        </View>
      ) : null}
    </>
  );
};

const useGoalSearchFiltersStyles = createThemedStyles((colors) => ({
  searchFilterContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    backgroundColor: colors.surface,
    gap: 12,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.grey[100],
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: isWeb ? 12 : 10,
    minHeight: isWeb ? 48 : 44,
    flex: 1,
    marginRight: 8,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: colors.text.primary,
    paddingVertical: isWeb ? 4 : 2,
    ...(isWeb ? { outlineStyle: 'none' as const } : {}),
  },
  clearButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: colors.surface,
  },
  filterToggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
    backgroundColor: colors.primary.background,
    borderRadius: 8,
    minWidth: 80,
    justifyContent: 'center',
  },
  filterToggleText: {
    marginLeft: 4,
    color: colors.button.primary,
    fontSize: 14,
    fontWeight: 'bold',
  },
  filterOptionsContainer: {
    marginHorizontal: 16,
    marginBottom: 8,
    padding: 16,
    backgroundColor: colors.surface,
    borderRadius: 8,
  },
  filterSection: {
    marginBottom: 16,
  },
  filterSectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: colors.text.primary,
    marginBottom: 8,
  },
  filterChipsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  filterChip: {
    alignItems: 'center',
    padding: 8,
    borderWidth: 2,
    borderRadius: 8,
    borderColor: colors.grey[300],
    minWidth: 80,
  },
  filterChipSelected: {
    backgroundColor: colors.primary.background,
  },
  filterChipText: {
    fontSize: 14,
    color: colors.text.secondary,
  },
  filterChipTextSelected: {
    color: colors.button.primary,
    fontWeight: 'bold',
  },
  filterChipIcon: {
    fontSize: 24,
    marginBottom: 4,
  },
  clearFiltersButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: colors.surface,
  },
  clearFiltersButtonText: {
    color: colors.error.main,
    fontSize: 14,
    fontWeight: 'bold',
  },
}));

export default GoalSearchFilters;
